import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Filter, SlidersHorizontal, Search, X } from "lucide-react";
import { api } from "../lib/api";
import VehicleCard from "../components/VehicleCard";
import DemoBanner from "../components/DemoBanner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";

const DEFAULT_FILTERS = {
  brand: "",
  fuel: "",
  country: "",
  min_ai_score: 0,
  min_margin: 0,
  km_max: null,
  year_min: null,
  year_max: null,
  price_min: null,
  price_max: null,
  q: "",
};

export default function MarketplacePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialSort = searchParams.get("sort") || "margin";

  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState(initialSort);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const LIMIT = 24;

  useEffect(() => {
    api.get("/meta/filters").then((r) => setMeta(r.data));
  }, []);

  const buildParams = useCallback(
    (nextOffset = 0) => {
      const p = { sort, limit: LIMIT, offset: nextOffset };
      if (filters.brand) p.brand = filters.brand;
      if (filters.fuel) p.fuel = filters.fuel;
      if (filters.country) p.country = filters.country;
      if (filters.min_ai_score > 0) p.min_ai_score = filters.min_ai_score;
      if (filters.min_margin > 0) p.min_margin = filters.min_margin;
      if (filters.km_max) p.km_max = filters.km_max;
      if (filters.year_min) p.year_min = filters.year_min;
      if (filters.year_max) p.year_max = filters.year_max;
      if (filters.price_min) p.price_min = filters.price_min;
      if (filters.price_max) p.price_max = filters.price_max;
      if (filters.q) p.q = filters.q;
      return p;
    },
    [filters, sort]
  );

  const fetchData = useCallback(
    async (append = false) => {
      setLoading(true);
      const nextOffset = append ? offset + LIMIT : 0;
      const params = buildParams(nextOffset);
      try {
        const { data } = await api.get("/vehicles", { params });
        setTotal(data.total);
        setOffset(nextOffset);
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      } finally {
        setLoading(false);
      }
    },
    [buildParams, offset]
  );

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, filters]);

  const canLoadMore = items.length < total;

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.brand) n++;
    if (filters.fuel) n++;
    if (filters.country) n++;
    if (filters.min_ai_score > 0) n++;
    if (filters.min_margin > 0) n++;
    if (filters.km_max) n++;
    if (filters.year_min || filters.year_max) n++;
    if (filters.price_min || filters.price_max) n++;
    return n;
  }, [filters]);

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  return (
    <div className="py-6 md:py-10">
      <DemoBanner testid="marketplace-demo-banner" />

      {/* Toolbar */}
      <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
            {t("marketplace.overline")}
          </p>
          <h1 className="mt-1 font-heading font-bold text-3xl md:text-4xl tracking-tighter text-slate-950">
            {t("marketplace.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="marketplace-count">
            {t("marketplace.count_result", { count: total })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="marketplace-search"
              placeholder={t("marketplace.search_placeholder")}
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              className="pl-9 h-10 rounded-full bg-white border-slate-200"
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger
              data-testid="marketplace-sort"
              className="h-10 rounded-full bg-white border-slate-200 min-w-[170px]"
            >
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ai_score">{t("marketplace.sort_options.ai_score")}</SelectItem>
              <SelectItem value="margin">{t("marketplace.sort_options.margin")}</SelectItem>
              <SelectItem value="price_asc">{t("marketplace.sort_options.price_asc")}</SelectItem>
              <SelectItem value="price_desc">{t("marketplace.sort_options.price_desc")}</SelectItem>
              <SelectItem value="newest">{t("marketplace.sort_options.newest")}</SelectItem>
            </SelectContent>
          </Select>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="filters-open"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
              >
                <Filter className="w-4 h-4" />
                {t("marketplace.filters")}
                {activeFilterCount > 0 && (
                  <span className="ml-1 text-[10px] bg-white text-slate-900 rounded-full px-1.5 py-0.5 tabular-nums">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="font-heading tracking-tight">
                  {t("marketplace.filters")}
                </SheetTitle>
                <SheetDescription className="text-xs text-slate-500">
                  {t("marketplace.count_result", { count: total })}
                </SheetDescription>
              </SheetHeader>
              <FiltersPanel filters={filters} setFilters={setFilters} meta={meta} />
              <div className="mt-6 flex items-center gap-2">
                <Button
                  variant="outline"
                  data-testid="filters-reset"
                  onClick={resetFilters}
                  className="flex-1 rounded-full"
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  {t("marketplace.reset")}
                </Button>
                <Button
                  data-testid="filters-apply"
                  onClick={() => setSheetOpen(false)}
                  className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700"
                >
                  {t("marketplace.apply_filters")}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Grid */}
      {items.length === 0 && !loading ? (
        <div
          data-testid="marketplace-empty"
          className="text-center py-24 text-slate-500 text-sm max-w-md mx-auto"
        >
          {activeFilterCount === 0 && !filters.q
            ? t("marketplace.empty_no_inventory")
            : t("marketplace.empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {items.map((v) => (
            <VehicleCard key={v.id} vehicle={v} />
          ))}
        </div>
      )}

      {canLoadMore && (
        <div className="mt-8 flex justify-center">
          <Button
            variant="outline"
            data-testid="marketplace-load-more"
            onClick={() => fetchData(true)}
            disabled={loading}
            className="rounded-full px-8"
          >
            {t("marketplace.load_more")}
          </Button>
        </div>
      )}
    </div>
  );
}

function FiltersPanel({ filters, setFilters, meta }) {
  const { t } = useTranslation();
  if (!meta) return <div className="mt-6 text-sm text-slate-400">…</div>;

  return (
    <div className="mt-6 space-y-6">
      {/* Brand */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {t("marketplace.filter.brand")}
        </label>
        <Select
          value={filters.brand || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, brand: v === "all" ? "" : v }))}
        >
          <SelectTrigger data-testid="filter-brand" className="mt-1.5 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("marketplace.filter.all_brands")}</SelectItem>
            {meta.brands.map((b) => (
              <SelectItem key={b} value={b}>
                {b}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Fuel */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {t("marketplace.filter.fuel")}
        </label>
        <Select
          value={filters.fuel || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, fuel: v === "all" ? "" : v }))}
        >
          <SelectTrigger data-testid="filter-fuel" className="mt-1.5 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("marketplace.filter.all_fuels")}</SelectItem>
            {meta.fuels.map((f) => (
              <SelectItem key={f} value={f}>
                {t(`fuel.${f}`, f)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Country */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {t("marketplace.filter.country")}
        </label>
        <Select
          value={filters.country || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, country: v === "all" ? "" : v }))}
        >
          <SelectTrigger data-testid="filter-country" className="mt-1.5 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("marketplace.filter.all_countries")}</SelectItem>
            {meta.countries.map((c) => (
              <SelectItem key={c} value={c}>
                {c === "IT" ? "🇮🇹 Italia" : c === "DE" ? "🇩🇪 Germania" : c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* AI Score slider */}
      <div>
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-500 uppercase tracking-wider">
            {t("marketplace.filter.min_ai_score")}
          </span>
          <span
            data-testid="filter-min-ai-score-value"
            className="font-semibold text-slate-900 tabular-nums"
          >
            {filters.min_ai_score || 0}
          </span>
        </div>
        <Slider
          data-testid="filter-min-ai-score"
          value={[filters.min_ai_score || 0]}
          onValueChange={([v]) => setFilters((f) => ({ ...f, min_ai_score: v }))}
          min={0}
          max={100}
          step={5}
          className="mt-3"
        />
      </div>

      {/* Min margin */}
      <div>
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-500 uppercase tracking-wider">
            {t("marketplace.filter.min_margin")}
          </span>
          <span
            data-testid="filter-min-margin-value"
            className="font-semibold text-slate-900 tabular-nums"
          >
            {(filters.min_margin || 0).toLocaleString("it-IT")} €
          </span>
        </div>
        <Slider
          data-testid="filter-min-margin"
          value={[filters.min_margin || 0]}
          onValueChange={([v]) => setFilters((f) => ({ ...f, min_margin: v }))}
          min={0}
          max={15000}
          step={500}
          className="mt-3"
        />
      </div>

      {/* Price range */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {t("marketplace.filter.price_range")}
        </label>
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          <Input
            data-testid="filter-price-min"
            type="number"
            placeholder="Min"
            value={filters.price_min || ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, price_min: e.target.value ? Number(e.target.value) : null }))
            }
            className="rounded-xl"
          />
          <Input
            data-testid="filter-price-max"
            type="number"
            placeholder="Max"
            value={filters.price_max || ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, price_max: e.target.value ? Number(e.target.value) : null }))
            }
            className="rounded-xl"
          />
        </div>
      </div>

      {/* Year range */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {t("marketplace.filter.year_range")}
        </label>
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          <Input
            data-testid="filter-year-min"
            type="number"
            placeholder={String(meta.year_min)}
            value={filters.year_min || ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, year_min: e.target.value ? Number(e.target.value) : null }))
            }
            className="rounded-xl"
          />
          <Input
            data-testid="filter-year-max"
            type="number"
            placeholder={String(meta.year_max)}
            value={filters.year_max || ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, year_max: e.target.value ? Number(e.target.value) : null }))
            }
            className="rounded-xl"
          />
        </div>
      </div>

      {/* Km max */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {t("marketplace.filter.km_max")}
        </label>
        <Input
          data-testid="filter-km-max"
          type="number"
          placeholder={String(meta.km_max)}
          value={filters.km_max || ""}
          onChange={(e) =>
            setFilters((f) => ({ ...f, km_max: e.target.value ? Number(e.target.value) : null }))
          }
          className="mt-1.5 rounded-xl"
        />
      </div>
    </div>
  );
}
